import { Fragment, ReactNode, useMemo, useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNotify } from "react-admin";
import { StatusLabel } from "../Label/status";

export type AuditKind = "created" | "status" | "payment" | "refund" | "kyt" | "sweep" | "conversion" | "quote" | "approval" | "other";

// One event of a trail, whatever its source (order, GasStation transfer).
export type AuditItem = {
  id: string | number;
  at: string;
  kind: AuditKind;
  actor: string;
  isSystem: boolean;
  text?: string;
  from?: string;
  to?: string;
};

const KINDS: Record<AuditKind, { label: string; color: string }> = {
  created: { label: "Created", color: "primary.main" },
  status: { label: "Status", color: "info.main" },
  payment: { label: "Payment", color: "success.main" },
  refund: { label: "Refund", color: "warning.main" },
  kyt: { label: "KYT", color: "error.main" },
  sweep: { label: "Sweep", color: "secondary.main" },
  conversion: { label: "Conversion", color: "success.dark" },
  quote: { label: "Quote", color: "grey.500" },
  approval: { label: "Approval", color: "primary.dark" },
  other: { label: "Event", color: "grey.400" },
};

// Hashes, wallet ids, UUIDs and addresses: long tokens mixing letters and digits.
const IDENTIFIER = /\b(?:0x)?(?=[A-Za-z0-9_-]*\d)(?=[A-Za-z0-9_-]*[A-Za-z])[A-Za-z0-9_-]{24,}\b/g;

const Identifier = ({ value }: { value: string }) => {
  const { t }: { t: any } = useTranslation();
  const notify = useNotify();
  return (
    <Tooltip title={`${value} · ${t("Click to copy")}`}>
      <Box
        component="span"
        onClick={() => {
          navigator.clipboard.writeText(value);
          notify(t("Copied to clipboard"), { type: "success" });
        }}
        sx={{
          fontFamily: "monospace",
          fontSize: 12,
          px: 0.5,
          py: 0.1,
          borderRadius: 0.5,
          bgcolor: "grey.100",
          cursor: "pointer",
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: "grey.200" },
        }}
      >
        {`${value.slice(0, 6)}…${value.slice(-4)}`}
      </Box>
    </Tooltip>
  );
};

// A line with its identifiers shortened (full value on hover, copied on click).
const Text = ({ text }: { text: string }) => {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of Array.from(text.matchAll(IDENTIFIER))) {
    const start = match.index ?? 0;
    parts.push(text.slice(last, start), <Identifier key={start} value={match[0]} />);
    last = start + match[0].length;
  }
  parts.push(text.slice(last));
  return (
    <Typography variant="body2" sx={{ wordBreak: "break-word", lineHeight: 1.7 }}>
      {parts}
    </Typography>
  );
};

const Transition = ({ from, to }: { from: string; to: string }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ my: 0.5 }}>
    <StatusLabel fontSize="small" record={{ statusOrder: from }} />
    <ArrowForwardIcon sx={{ fontSize: 16, color: "text.secondary" }} />
    <StatusLabel fontSize="small" record={{ statusOrder: to }} />
  </Stack>
);

const day = (at: string) => {
  const text = new Date(at).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const time = (at: string) => new Date(at).toLocaleTimeString("en-GB");

const Entry = ({ item, last }: { item: AuditItem; last: boolean }) => {
  const { t }: { t: any } = useTranslation();
  const kind = KINDS[item.kind];
  return (
    <Box display="flex" gap={2}>
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: 12, flexShrink: 0 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: kind.color, mt: 0.75, flexShrink: 0 }} />
        {!last && <Box sx={{ flex: 1, width: "2px", bgcolor: "divider", mt: 0.5 }} />}
      </Box>
      <Box flex={1} minWidth={0} pb={2.5}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={700}>
            {t(kind.label)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "none" }}>
            {time(item.at)} · {item.isSystem ? t(item.actor) : item.actor}
          </Typography>
        </Stack>
        {item.from && item.to && <Transition from={item.from} to={item.to} />}
        {item.text && <Text text={item.text} />}
      </Box>
    </Box>
  );
};

/**
 * A trail as a timeline, most recent first, grouped by day, with a filter per kind of
 * event when there are several.
 */
const AuditTimeline = ({ items }: { items: AuditItem[] }) => {
  const { t }: { t: any } = useTranslation();
  const [kind, setKind] = useState<AuditKind | null>(null);

  const counts = useMemo(() => {
    const byKind = new Map<AuditKind, number>();
    for (const item of items) byKind.set(item.kind, (byKind.get(item.kind) || 0) + 1);
    return byKind;
  }, [items]);

  const shown = kind ? items.filter((item) => item.kind === kind) : items;
  const days: { day: string; items: AuditItem[] }[] = [];
  for (const item of shown) {
    const label = day(item.at);
    if (days[days.length - 1]?.day !== label) days.push({ day: label, items: [] });
    days[days.length - 1].items.push(item);
  }

  return (
    <Box>
      {counts.size > 1 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
          <Chip label={`${t("All")} · ${items.length}`} size="small" color={kind ? "default" : "secondary"} variant={kind ? "outlined" : "filled"} onClick={() => setKind(null)} />
          {(Object.keys(KINDS) as AuditKind[])
            .filter((key) => counts.has(key))
            .map((key) => (
              <Chip
                key={key}
                size="small"
                label={`${t(KINDS[key].label)} · ${counts.get(key)}`}
                color={kind === key ? "secondary" : "default"}
                variant={kind === key ? "filled" : "outlined"}
                onClick={() => setKind(kind === key ? null : key)}
              />
            ))}
        </Stack>
      )}
      {days.map((group) => (
        <Fragment key={group.day}>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1, textTransform: "none", letterSpacing: 0.5 }}>
            {group.day}
          </Typography>
          {group.items.map((item, index) => (
            <Entry key={item.id} item={item} last={index === group.items.length - 1} />
          ))}
        </Fragment>
      ))}
    </Box>
  );
};

export default AuditTimeline;
